
import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { SmsTemplate } from '@/hooks/sms/types';
import { formatDistanceToNow } from 'date-fns';

interface TemplatesTableProps {
  templates: SmsTemplate[];
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onTemplateDelete?: () => void;
}

const TemplatesTable: React.FC<TemplatesTableProps> = ({
  templates,
  onEdit,
  onDuplicate,
  onDelete,
  onTemplateDelete
}) => {
  if (!templates || templates.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        No templates found.
      </div>
    );
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never used';
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>Preview</TableHead>
          <TableHead className="text-center">Last Used</TableHead>
          <TableHead className="text-center">Usage</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {templates.map((template) => (
          <TableRow key={template.id}>
            <TableCell className="font-medium">{template.name}</TableCell>
            <TableCell>
              {/* Add debugging info to verify category */}
              <Badge variant="outline" className="capitalize">
                {template.category || 'General'}
              </Badge>
              {template.category !== 'General' && template.category && (
                <span className="ml-1 text-xs text-muted-foreground hidden">
                  (ID: {template.id})
                </span>
              )}
            </TableCell>
            <TableCell>
              <div className="max-w-[200px] truncate text-sm text-muted-foreground">
                {template.content || template.preview || 'No content'}
              </div>
            </TableCell>
            <TableCell className="text-center text-sm">
              {formatDate(template.lastUsed)}
            </TableCell>
            <TableCell className="text-center">
              <Badge variant="secondary">{template.usageCount || 0}</Badge>
            </TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(template.id)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicate(template.id)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(template.id)} 
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TemplatesTable;
